/**
 * Persist GraphQL query results in IndexedDB, keyed by user + operation + variables.
 */
import { CACHEABLE_OPERATIONS, QUEUEABLE_QUERY_OPERATIONS, STORE_GQL } from "./constants";
import { idbGet, idbPut } from "./idb";

/**
 * Reads the `id` claim from the stored JWT without verifying the signature.
 *
 * @returns {string} User id or `anon`.
 */
export const readJwtUserId = () => {
  const jwt = (typeof localStorage === "undefined" ? "" : localStorage.getItem("jwt-ghost") || localStorage.getItem("jwt")) || "";
  const part = jwt.split(".")[1];
  if (!part) return "anon";
  try {
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(padded));
    return json.id != null ? String(json.id) : "anon";
  } catch {
    return "anon";
  }
};

/**
 * Parses the first named GraphQL operation from a document string.
 *
 * @param {string} query GraphQL document.
 * @returns {string} Operation name, or empty string.
 */
export const getOperationName = (query) => {
  const match = String(query || "").match(/\b(?:query|mutation|subscription)\s+([A-Za-z_][\w]*)/);
  return match ? match[1] : "";
};

/**
 * True when a GraphQL document is a mutation (named or anonymous).
 *
 * @param {string} query GraphQL document.
 * @returns {boolean}
 */
export const isMutationDocument = (query) => /\bmutation\b/.test(String(query || ""));

/** Presence and typing are live-only and must not sit in the offline queue. */
const EPHEMERAL_OPERATIONS = new Set(["presence_ping", "presence_leave", "chat_typing"]);

/**
 * True when a failed network call should be stored and replayed later.
 *
 * @param {string} query GraphQL document.
 * @returns {boolean}
 */
export const isQueueableDocument = (query) => {
  if (EPHEMERAL_OPERATIONS.has(getOperationName(query))) return false;
  if (isMutationDocument(query)) return true;
  return QUEUEABLE_QUERY_OPERATIONS.has(getOperationName(query));
};

/**
 * Value returned to callers while a mutation waits in the offline queue.
 * `comment_create` callers treat the result as an id.
 *
 * @param {string} query GraphQL document.
 * @returns {*|true}
 */
export const queuedMutationFallback = (query) => {
  if (getOperationName(query) === "comment_create") return `offline-${Date.now()}`;
  return true;
};

/**
 * Builds a stable cache key for a GraphQL call.
 *
 * @param {string} query GraphQL document.
 * @param {object} [variables={}] Variables.
 * @returns {string|null} Key, or null when the operation must not be cached.
 */
export const buildCacheKey = (query, variables = {}) => {
  const operation = getOperationName(query);
  if (!CACHEABLE_OPERATIONS.has(operation)) return null;
  return `${readJwtUserId()}::${operation}::${JSON.stringify(variables || {})}`;
};

/**
 * Writes a successful GraphQL payload into IndexedDB.
 *
 * @param {string} query GraphQL document.
 * @param {object} variables Variables.
 * @param {*} value Unwrapped field value.
 * @returns {Promise<void>}
 */
export const putGqlCache = async (query, variables, value) => {
  const key = buildCacheKey(query, variables);
  if (!key) return;
  try {
    await idbPut(STORE_GQL, {
      key,
      operation: getOperationName(query),
      value,
      cachedAt: Date.now(),
    });
  } catch {
    /* quota / private mode */
  }
};

/**
 * Reads a previously stored GraphQL payload.
 *
 * @param {string} query GraphQL document.
 * @param {object} variables Variables.
 * @returns {Promise<*|undefined>}
 */
export const getGqlCache = async (query, variables) => {
  const key = buildCacheKey(query, variables);
  if (!key) return undefined;
  try {
    const row = await idbGet(STORE_GQL, key);
    return row ? row.value : undefined;
  } catch {
    return undefined;
  }
};
